import { IDocumentAssemblerGenerationPlaceholder } from '@commenda/api';

import type {
  DocumentAssemblerSurroundingContextGroup,
  DocumentAssemblerSurroundingContextSegment,
} from './document-assembler.types';
import { DocumentAssemblerHelper } from './DocumentAssemblerHelper';

export class DocumentAssemblerGenerationSurroundingContextHelper {
  static groupByParagraph(
    placeholders: IDocumentAssemblerGenerationPlaceholder[]
  ): DocumentAssemblerSurroundingContextGroup[] {
    const placeholdersByParagraphKey = new Map<
      string,
      IDocumentAssemblerGenerationPlaceholder[]
    >();

    for (const placeholder of placeholders) {
      const { paragraphKey } = placeholder.placeholdersSurroundingContext;

      placeholdersByParagraphKey.set(paragraphKey, [
        ...(placeholdersByParagraphKey.get(paragraphKey) ?? []),
        placeholder,
      ]);
    }

    const groups: DocumentAssemblerSurroundingContextGroup[] = [];
    placeholdersByParagraphKey.forEach((grouped, paragraphKey) =>
      groups.push({
        paragraphKey,
        placeholders: [...grouped].sort(
          (left, right) =>
            left.placeholdersSurroundingContext.prefix.length -
            right.placeholdersSurroundingContext.prefix.length
        ),
        isEveryPlaceholderLeftOut: grouped.every((placeholder) =>
          DocumentAssemblerHelper.isLeftOut(placeholder)
        ),
      })
    );

    return groups.sort(
      (left, right) => this.lowestOrdinal(left) - this.lowestOrdinal(right)
    );
  }

  static toSegments(
    group: DocumentAssemblerSurroundingContextGroup
  ): DocumentAssemblerSurroundingContextSegment[] {
    const segments: DocumentAssemblerSurroundingContextSegment[] = [];
    let prefixLengthAlreadyRendered = 0;

    for (const placeholder of group.placeholders) {
      const { prefix, tag } = placeholder.placeholdersSurroundingContext;
      const proseBeforeSlot = prefix.slice(prefixLengthAlreadyRendered);

      if (proseBeforeSlot) {
        segments.push({
          kind: 'PROSE',
          key: `${placeholder.id}-before`,
          text: proseBeforeSlot,
        });
      }

      segments.push({ kind: 'SLOT', key: placeholder.id, placeholder });
      prefixLengthAlreadyRendered = prefix.length + tag.length;
    }

    const lastPlaceholder = group.placeholders[group.placeholders.length - 1];
    const trailingProse =
      lastPlaceholder?.placeholdersSurroundingContext.suffix ?? '';

    if (trailingProse) {
      segments.push({
        kind: 'PROSE',
        key: `${group.paragraphKey}-after`,
        text: trailingProse,
      });
    }

    return segments;
  }

  private static lowestOrdinal(
    group: DocumentAssemblerSurroundingContextGroup
  ): number {
    return Math.min(
      ...group.placeholders.map((placeholder) => placeholder.ordinal)
    );
  }
}
